import { Container, Graphics, SCALE_MODES, Sprite, Texture } from "pixi.js"
import { Mechanics } from "game-common/mechanics/mechanics"
import { AnimatedFeedbackParams, Entity, EntityId } from "game-common/models"

/**
 * The dust a running body kicks up (ENTITY_FEEDBACK `type: "foot"` -- a player holding the boost
 * key, or an NPC rushing a target).
 *
 * WHY THIS IS ITS OWN EFFECT. The trail used to be built by the generic AnimatedFeedback, which
 * parents everything it makes into the WORLD OVERLAY surface -- the layer that carries nameplates
 * and chat bubbles, added to the stage after the world and therefore drawn on top of every entity
 * in the room. That is right for a nameplate and wrong for dust: a runner heading SOUTH leaves its
 * trail to the north of itself, where the puffs have to be drawn BEHIND the body, and they were
 * drawn in front of it instead (and over every other character they passed). Nothing about the old
 * placement could be tuned into the right answer, because the overlay has no relationship to the
 * entity z-order at all. So the trail lives here, in the world surface, where each puff is an
 * ordinary z-sorted sibling of the bodies it has to interleave with.
 *
 * WHAT MAKES IT READ AS KICKED-UP DUST rather than as a sprite playing over the runner. Three
 * things, none of which the old version did:
 *
 *   FOOTFALLS, NOT A COLUMN. Puffs come off alternating feet, offset PERPENDICULAR to the direction
 *   of travel. One emission point dead-centre on the body draws a single column of stamps; two
 *   interleaved tracks read as something a pair of shoes did.
 *
 *   KICKED BACKWARDS, NOT RISING. Each puff is thrown along the ground opposite the run and
 *   decelerates into stillness (exponential drag), so it falls behind the runner and settles where
 *   it landed. Separately -- and this is the part that needs the 3/4 view taken seriously -- it has
 *   a HEIGHT that is tracked apart from its ground position: `lift` moves the drawn sprite up the
 *   screen while `groundY` stays put, so a rising puff does not slide backwards out of its own
 *   footprint, and it still sorts against bodies by the patch of ground it came off.
 *
 *   DIFFUSING, NOT STAMPED. It fades in over the first tenth of its life, grows to about 1.7x, and
 *   fades out on a curve. Size, lifetime, tint, spin and kick are all jittered per puff, so a run
 *   never shows the same puff twice.
 *
 * The art is baked here rather than shipped (the pattern wind_leaves and butterflies established):
 * white lumps generated once, tinted per puff off an earth palette, which is what lets one texture
 * serve the whole trail and batch into a single draw call. The shipped `dust` spritesheet it
 * replaces was an 8-frame burst of 1px GREY specks that popped symmetrically outward -- a pop, not
 * a kick, and grey flecks on grass at that.
 *
 * NOTHING HERE USES A TIMER. One update() off the render loop advances every puff of every trail
 * and decides when the next footfall lands. That matters twice over: a dozen rushing NPCs would
 * otherwise each hold a setTimeout chain plus one setInterval per live puff, and in a backgrounded
 * tab (rAF throttled to zero) the timers would keep emitting into a world nobody is drawing.
 */

// How far BELOW `entity.location.y` an LPC humanoid's soles are drawn: location.y lands around the
// hips, not on the floor. A puff is kicked up by a shoe, so it is laid down at the feet -- but
// sorted on the hip line, which is the line every entity in the surface sorts by. Positioning at the
// feet and sorting at the hip is the whole trick; sorting at the feet instead would draw the dust in
// front of its own runner from the waist down.
const FOOT_DROP = 20

// Dust comes off the back of the heel, so a puff sorts just behind whatever is standing on the same
// patch of ground. It only decides anything in the instant a puff is born, when it and the runner
// share a sort line; after that each one sorts on its own ground position.
const BEHIND_THE_HEELS = 1

// One footfall per this many ms, alternating feet. Roughly a stride at run speed.
const FOOTFALL_MS = 105
// A trail normally ends on the matching `action: "remove"` (BOOST_ACTION_OFF, or a rushing NPC
// arriving). Nothing guarantees that event lands -- it is a distance-filtered broadcast, and the
// runner can be culled or leave the room mid-stride -- so a trail also expires by itself.
const TRAIL_MAX_MS = 1500
// Emission is driven off the render loop, so a backgrounded tab (or a long frame) can hand us a gap
// of seconds. Past this many footfalls' worth we skip the backlog rather than burst-emitting it.
const MAX_FOOTFALL_CATCHUP = 4
// A frame longer than this is a stall, not motion; clamp it so nothing teleports on the way back.
const MAX_FRAME_SECONDS = 0.05

// Where a footfall lands relative to the body: half the gap between the two feet, across the
// direction of travel, and a little behind the body's centre where the pushing foot actually is.
const FOOT_SPREAD = 4.5
const HEEL_SETBACK = 3

// The kick, in world px/sec along the ground, opposite the run. GROUND_DRAG is an e-fold per second:
// at 5.5 a puff has spent most of its travel inside 200ms, so the trail's spacing comes from the
// runner moving on rather than from the dust flying.
const KICK = 34
const KICK_MIN = 0.65
const KICK_MAX = 1.35
const GROUND_DRAG = 5.5
// How far off straight-backwards a puff may be thrown, in radians either way.
const KICK_SPREAD = 0.5

// Height off the ground, which is tracked and drawn separately from the ground position (see above).
const LIFT = 15
const LIFT_DRAG = 3.4

// Per-puff jitter ranges.
const SCALE_MIN = 0.42
const SCALE_MAX = 0.62
const GROWTH_MIN = 1.5
const GROWTH_MAX = 2.0
const ALPHA_MIN = 0.5
const ALPHA_MAX = 0.78
const SPIN = 0.6
const LIFE_MS = 520
const LIFE_JITTER = 0.3
// Fraction of its life a puff holds full opacity for before it starts fading.
const FADE_AFTER = 0.42
// Fraction of its life it fades IN over, so it diffuses into being instead of popping.
const FADE_IN = 0.1

// Extra, smaller motes thrown with each footfall. One is enough to break the silhouette up; the
// difference between a single lump and a lump with a mote beside it is most of the irregularity.
const SATELLITES = 1
const SATELLITE_SCALE = 0.55

// Dry earth catching the light. Varied per puff rather than per trail, for grain.
const TINTS = [0xd8caa8, 0xc4b494, 0xb3a384, 0xe3d8bd]

// Total live puffs across every trail. A wave of rushing NPCs is the case this exists for: they all
// kick dust at once, and the trail is ambience -- dropping the newest puff is invisible, a frame
// spent on four hundred sprites is not.
const MAX_PUFFS = 220

interface Puff {
    sprite: Sprite
    // Who kicked it up, so remove() can take a destroyed entity's trail off the surface.
    emitterId: EntityId
    // Where on the GROUND it is, and how high off it. Drawn at (x, groundY - lift), sorted on
    // groundY: a puff that rises must not slide backwards out of its own footprint.
    x: number
    groundY: number
    lift: number
    vx: number
    vy: number
    liftV: number
    bornMs: number
    lifeMs: number
    // Sort line = zBase + groundY, where zBase carries the emitter's race class (the engine's crude
    // 100 humanoid / 90 creature / 85 small split) and the hip-vs-feet correction.
    zBase: number
    scale: number
    growth: number
    alpha: number
    spin: number
}

interface Trail {
    entity: Entity
    roomId: string
    startedMs: number
    nextFootfallMs: number
    // Which foot is next. Flipping this is what turns a column of stamps into two tracks.
    side: number
    lifeMs: number
}

const between = (min: number, max: number): number => min + Math.random() * (max - min)

export class FootDust {
    private surface: Container
    private makeTextures: () => Texture[]
    private textures: Texture[] = []
    private trails: Map<EntityId, Trail> = new Map()
    private puffs: Puff[] = []
    private lastMs?: number

    constructor(surface: Container, makeTextures: () => Texture[]) {
        this.surface = surface
        this.makeTextures = makeTextures
    }

    // Begin (or restart) the trail under `entity`. `params.duration` sets the base puff lifetime;
    // the art is baked here, so the event's `image` is not looked up.
    start = (entity: Entity, params: AnimatedFeedbackParams = {}, nowMs: number = performance.now()) => {
        // A second start while one is already running -- an NPC that re-rushes without arriving --
        // replaces the emitter rather than stacking a second one onto the same entity.
        this.stop(entity.entityId)
        this.trails.set(entity.entityId, {
            entity,
            roomId: entity.roomId,
            startedMs: nowMs,
            nextFootfallMs: nowMs,
            side: Math.random() < 0.5 ? 1 : -1,
            lifeMs: params?.duration || LIFE_MS,
        })
    }

    // Stop emitting under `entity`. Puffs already on the ground are left to settle and fade -- dust
    // does not vanish the moment a runner stops running.
    stop = (entityId: EntityId) => {
        this.trails.delete(entityId)
    }

    // Stop emitting AND take the trail off the surface now. For an entity being destroyed, or one
    // that changed rooms: its dust belongs to a patch of ground that is no longer on screen.
    remove = (entityId: EntityId) => {
        const trail = this.trails.get(entityId)
        this.stop(entityId)
        if (!trail) {
            return
        }
        const emitter = trail.entity.entityId
        this.puffs = this.puffs.filter(puff => {
            if (puff.emitterId !== emitter) {
                return true
            }
            this.retire(puff)
            return false
        })
    }

    // Exposed for tests: puffs currently on the surface.
    activeCount = (): number => this.puffs.length

    /**
     * Lay down any footfalls that have come due and advance every live puff. Called once per drawn
     * frame (EntityRenderingManager.endFrame), which is also what pauses the whole effect in a
     * backgrounded tab rather than letting it emit into a world nobody is drawing.
     */
    update = (nowMs: number = performance.now()) => {
        if (this.lastMs === undefined) {
            this.lastMs = nowMs
            return
        }
        const dt = Math.min((nowMs - this.lastMs) / 1000, MAX_FRAME_SECONDS)
        this.lastMs = nowMs

        this.emit(nowMs)
        this.advance(nowMs, dt)
    }

    private emit = (nowMs: number) => {
        this.trails.forEach((trail, entityId) => {
            const entity = trail.entity
            if (entity.roomId !== trail.roomId) {
                this.remove(entityId)
                return
            }
            if (nowMs - trail.startedMs > TRAIL_MAX_MS) {
                this.stop(entityId)
                return
            }
            // A stalled frame (or a tab coming back) can leave several footfalls due at once. Lay
            // down one and skip the backlog -- emitting it would dump a clump of dust on one spot.
            if (nowMs - trail.nextFootfallMs > FOOTFALL_MS * MAX_FOOTFALL_CATCHUP) {
                trail.nextFootfallMs = nowMs
            }
            while (nowMs >= trail.nextFootfallMs) {
                this.footfall(trail, nowMs)
                trail.nextFootfallMs += FOOTFALL_MS
            }
        })
    }

    // One foot hitting the ground: a lump of dust under the shoe, plus a smaller mote beside it.
    private footfall = (trail: Trail, nowMs: number) => {
        const entity = trail.entity
        // The direction of travel. `movement.angle` points the way the body is going (+y is south),
        // and is maintained for remote entities too -- the interpolator stamps it off the position
        // buffer -- so a trail behind another player is thrown the right way as well.
        const angle = entity.movement?.angle ?? 0
        const travelX = Math.cos(angle)
        const travelY = Math.sin(angle)
        // Across the run, for the gap between the two feet.
        const acrossX = -travelY
        const acrossY = travelX

        const side = trail.side
        trail.side = -side

        const footX = entity.location.x + acrossX * FOOT_SPREAD * side - travelX * HEEL_SETBACK
        const footGroundY = entity.location.y + FOOT_DROP + acrossY * FOOT_SPREAD * side - travelY * HEEL_SETBACK
        // The emitter's own sort line, minus its ground position: carrying the race class rather
        // than the position is what lets each puff sort on the ground it actually settles on.
        const zBase = Mechanics.entity.body.calculateZIndexBoost(entity) - entity.location.y - FOOT_DROP

        this.spawn(trail, nowMs, footX, footGroundY, travelX, travelY, zBase, 1)
        for (let i = 0; i < SATELLITES; i++) {
            this.spawn(trail, nowMs, footX, footGroundY, travelX, travelY, zBase, SATELLITE_SCALE)
        }
    }

    private spawn = (
        trail: Trail,
        nowMs: number,
        x: number,
        groundY: number,
        travelX: number,
        travelY: number,
        zBase: number,
        sizeFactor: number,
    ) => {
        if (this.puffs.length >= MAX_PUFFS) {
            return
        }
        const texture = this.texture()
        if (!texture) {
            return
        }

        // Thrown backwards along the ground, fanned off straight-back so a trail is not a ruler line.
        const spread = between(-KICK_SPREAD, KICK_SPREAD)
        const speed = KICK * between(KICK_MIN, KICK_MAX)
        const kickX = -(travelX * Math.cos(spread) - travelY * Math.sin(spread)) * speed
        const kickY = -(travelX * Math.sin(spread) + travelY * Math.cos(spread)) * speed

        const sprite = new Sprite(texture)
        // Most of the lump sits above the point it is pinned to, so it reads as sitting ON the
        // ground rather than straddling it, and it grows from there.
        sprite.anchor.set(0.5, 0.7)
        sprite.tint = TINTS[Math.floor(Math.random() * TINTS.length)]
        sprite.alpha = 0
        sprite.rotation = Math.random() * Math.PI * 2
        this.surface.addChild(sprite)

        const puff: Puff = {
            sprite,
            emitterId: trail.entity.entityId,
            x,
            groundY,
            lift: 0,
            vx: kickX,
            vy: kickY,
            liftV: LIFT * between(0.7, 1.3),
            bornMs: nowMs,
            lifeMs: trail.lifeMs * between(1 - LIFE_JITTER, 1 + LIFE_JITTER),
            zBase,
            scale: between(SCALE_MIN, SCALE_MAX) * sizeFactor,
            growth: between(GROWTH_MIN, GROWTH_MAX),
            alpha: between(ALPHA_MIN, ALPHA_MAX),
            spin: between(-SPIN, SPIN),
        }
        this.puffs.push(puff)
        this.draw(puff, 0)
    }

    private advance = (nowMs: number, dt: number) => {
        // Exponential decay: the kick is spent in the first fraction of a second and the puff settles
        // where it landed, instead of coasting at a constant speed for its whole life.
        const groundDrag = Math.exp(-GROUND_DRAG * dt)
        const liftDrag = Math.exp(-LIFT_DRAG * dt)

        let write = 0
        for (let i = 0; i < this.puffs.length; i++) {
            const puff = this.puffs[i]
            const elapsed = (nowMs - puff.bornMs) / puff.lifeMs
            if (elapsed >= 1) {
                this.retire(puff)
                continue
            }

            puff.x += puff.vx * dt
            puff.groundY += puff.vy * dt
            puff.lift += puff.liftV * dt
            puff.vx *= groundDrag
            puff.vy *= groundDrag
            puff.liftV *= liftDrag
            puff.sprite.rotation += puff.spin * dt

            this.draw(puff, elapsed)
            this.puffs[write++] = puff
        }
        this.puffs.length = write
    }

    private draw = (puff: Puff, elapsed: number) => {
        const sprite = puff.sprite
        sprite.x = puff.x
        sprite.y = puff.groundY - puff.lift
        sprite.zIndex = puff.zBase + puff.groundY - BEHIND_THE_HEELS

        // Grows fastest early, the way a puff of anything diffuses.
        const eased = 1 - (1 - elapsed) * (1 - elapsed)
        sprite.scale.set(puff.scale * (1 + (puff.growth - 1) * eased))

        if (elapsed < FADE_IN) {
            sprite.alpha = puff.alpha * (elapsed / FADE_IN)
        } else if (elapsed < FADE_AFTER) {
            sprite.alpha = puff.alpha
        } else {
            const out = (elapsed - FADE_AFTER) / (1 - FADE_AFTER)
            sprite.alpha = puff.alpha * (1 - out * out)
        }
    }

    private retire = (puff: Puff) => {
        this.surface.removeChild(puff.sprite)
        puff.sprite.destroy()
    }

    // Baked on the first puff rather than at construction: the renderer may not be able to generate
    // a texture yet (and in a test there is no GPU at all), and a trail that silently never appears
    // is worse than one drawn with a plain white blob.
    private texture = (): Texture | undefined => {
        if (!this.textures.length) {
            try {
                this.textures = this.makeTextures()
            } catch (e) {
                this.textures = []
            }
            if (!this.textures.length) {
                this.textures = [Texture.WHITE]
            }
        }
        return this.textures[Math.floor(Math.random() * this.textures.length)]
    }

    /**
     * The puff art: three white lumps, each built from overlapping soft ellipses, which every puff
     * then tints off the earth palette. White art is what makes one texture serve the whole palette
     * (tint multiplies), and three silhouettes is enough that the eye stops reading a repeated stamp
     * while all three still batch together.
     *
     * The softness is BAKED, three nested ellipses per blob rather than a blur filter: a filtered
     * display object renders into its own framebuffer and cannot batch with its neighbours, which
     * would be an offscreen pass per puff with a dozen of them on screen.
     *
     * Drawn oversized and scaled down per puff, at resolution 2 to match the world surface's 2x zoom,
     * so the edges stay smooth instead of showing the blocky diagonal of a 10px sprite. Each lump is
     * wider than it is tall: dust spreads along the ground, and the ground is seen at an angle.
     */
    static buildPuffTextures = (generate: (g: Graphics) => Texture): Texture[] => {
        // [cx, cy, rx, ry] blobs per lump: a round one, a long low one, and a small wisp.
        const lumps: Array<Array<[number, number, number, number]>> = [
            [
                [0, 0, 7, 4.8],
                [-4.5, 1.4, 4.6, 3.4],
                [4, -1, 5, 3.6],
            ],
            [
                [0, 0, 8, 4.4],
                [-5, 0.6, 4, 2.8],
                [4.6, 1, 3.4, 2.4],
            ],
            [
                [0, 0, 5.4, 4],
                [3, -2, 3.6, 2.8],
            ],
        ]
        return lumps.map(blobs => {
            const g = new Graphics()
            blobs.forEach(([cx, cy, rx, ry]) => {
                // Faint halo, body, dense core. Overlapping blobs compound where they meet, which is
                // what gives a lump an uneven inside rather than a flat disc.
                g.beginFill(0xffffff, 0.22)
                g.drawEllipse(cx, cy, rx, ry)
                g.endFill()
                g.beginFill(0xffffff, 0.45)
                g.drawEllipse(cx, cy, rx * 0.68, ry * 0.68)
                g.endFill()
                g.beginFill(0xffffff, 0.9)
                g.drawEllipse(cx, cy, rx * 0.36, ry * 0.36)
                g.endFill()
            })
            const texture = generate(g)
            g.destroy()
            return texture
        })
    }

    // The default texture factory, bound to a live pixi renderer. Split from buildPuffTextures so the
    // trail can be driven in a test without a GPU.
    static textureFactory = (renderer: { generateTexture: (g: Graphics, options?: any) => Texture }) => (): Texture[] =>
        FootDust.buildPuffTextures(g => renderer.generateTexture(g, { resolution: 2, scaleMode: SCALE_MODES.LINEAR }))
}
